import Box from '@components/Box';
import Captcha from '@components/Captcha';
import CountrySelectContainer from '@containers/CountrySelectContainer';
import FormDocs from '@components/FormDocs';
import PasswordField from '@components/PasswordField';
import TextInputContainer from '@containers/TextInputContainer';
import Title from '@components/Title';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import { getCurrentLanguage, Langs } from '@utils/lang';
import { Grid, Column, Row } from '@components/Grid';
import { Text, TextSize, TextColor } from '@components/Text';
import { TSetState } from '@src/types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SignUpFormType {
  email: string;
  password: string;
  countryCode: string;
  ticket: string;
  randstr: string;
}

interface SignUpFormProps {
  handlerSetWeChatInformerVisible: TSetState<boolean>;
  onSubmit: (data: SignUpFormType) => void;
  pending: boolean;
}

const allowedCountries = ["CN", "TR"]

const SignUpForm = (props: SignUpFormProps) => {
  const {
    handlerSetWeChatInformerVisible,
    onSubmit,
    pending
  } = props;

  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get("lang");

  const currentLanguage = getCurrentLanguage();
  const defaultCountry = currentLanguage === Langs.tr ? "TR" : "CN";

  const { control, handleSubmit, watch, setValue } = useForm<SignUpFormType>({
    defaultValues: {
      email: "",
      password: "",
      countryCode: allowedCountries.find(c => (c === (urlLang || "").toUpperCase())) || defaultCountry,
      ticket: "",
      randstr: "",
    },
  });

  const { t } = useTranslation();
  const countryCode = watch('countryCode');
  const password = watch("password");
  const ticket = watch("ticket");

  useEffect(() => {
    handlerSetWeChatInformerVisible(countryCode === 'CN');
  }, [countryCode]);

  return (
    <Grid>
      <Row>
        <Column phone={12}>
          <Box pb={4}>
            <Title title={t("signUp.email.title")} />
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={12}>
          <Box pb={5}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.M}
            >
              {t("signUp.email.subTitle")}
            </Text>
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={12}>
          <Box pb={2}>
            <TextInputContainer
              control={control}
              name="email"
              type="email"
              title={t("signUp.email.inputEmail")}
              rules={{ required: t("signUp.email.emailErrorText") }}
            />
          </Box>
        </Column>
      </Row>
      <PasswordField<SignUpFormType>
          control={control}
          password={password}
          pb={5}
      />
      <Row>
        <Column phone={12}>
          <Box pb={5}>
            <CountrySelectContainer
              control={control}
              name="countryCode"
              allowedCountries={allowedCountries}
            />
          </Box>
        </Column>
      </Row>

      <Row>
        <Column phone={12}>
          <Box pb={8}>
            <Captcha
              onChange={(t, r) => {
                setValue("ticket", t);
                setValue("randstr", r);
              }}
            />
          </Box>
        </Column>
      </Row>

      <Row>
        <Column phone={12}>
          <Box pb={4}>
            <Button
              title={t("signUp.email.getAcc")}
              type={ButtonType.BLOCK}
              size={ButtonSize.S}
              onClick={handleSubmit(onSubmit)}
              pending={pending}
              disabled={!ticket}
            />
          </Box>
        </Column>
      </Row>

      <Row>
        <Column phone={12}>
          <Box>
            <FormDocs countryCode={countryCode} />
          </Box>
        </Column>
      </Row>
    </Grid>
  );
};

export default SignUpForm;
